import React, { useRef } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useStorage } from '~Components/Storage';
import { useAdyen, useDispatch, useLogger, useSelector } from '~Hooks';
import { useSocket } from '~Components/SocketManager';
import { MethodProps } from '../types';
import * as actions from '~actions';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';

import {
  AdyenPaymentFromBackend,
  AdyenPaymentFromBackendVariables,
  TerminalStatus,
} from './__queries__';

const adyenKey = process.env.ADYEN_KEY!;
const adyenPass = process.env.ADYEN_PASS!;

/* const securityKey = {
  adyenCryptoVersion: 1,
  keyIdentifier: adyenKey,
  keyVersion: 0,
  passphrase: adyenPass,
}; */

const BACKEND_ADYEN_PAYMENT = gql`
  query AdyenPaymentFromBackend($merchantReference: String!, $venueId: ID!) {
    AdyenPayment(id: $merchantReference, venueId: $venueId) {
      id
      method
      methodIdentifier
      createdAt
      pspReference
      tip
    }
  }
`;

const PAYMENT_STATUS_QUERY = gql`
  query TerminalStatus($terminalId: String!) {
    AdyenTerminalStatus(terminalId: $terminalId)
  }
`;

const AdyenPayment: React.FunctionComponent<MethodProps> = ({
  amount,
  orderAmount,
  order,
  discount,
  onComplete,
  onFail,
  onMetaChange,
  payment,
}) => {
  const venueId = useSelector((state) => state.venueId);
  const hasCancelled = useRef(false);
  const [storageData] = useStorage();
  const adyenTerminals = storageData.adyenTerminals;
  const adyen = useAdyen();
  const socket = useSocket();
  const logger = useLogger('payment:adyen');
  const dispatch = useDispatch();

  const terminalId = payment.variant || adyenTerminals[0]?.POIID;
  const isManagedTerminal = socket.managedTerminals.has(terminalId);

  const terminalStatusQuery = useQuery<TerminalStatus>(PAYMENT_STATUS_QUERY, {
    variables: {
      terminalId: terminalId,
    },
    skip: !terminalId,
    pollInterval: 1500,
    fetchPolicy: 'network-only',
  });
  const terminalStatus = terminalStatusQuery.data?.AdyenTerminalStatus;

  const [showStatus, setShowStatus] = useState(false);

  // Dont show the status until a few seconds in,
  // this is to prevent the status from a previous payment to show up
  useEffect(() => {
    setTimeout(() => {
      setShowStatus(true);
    }, 2500);
  }, []);

  useEffect(() => {
    if (!terminalStatus) {
      return;
    }

    if (!showStatus) {
      return;
    }

    onMetaChange({
      terminalStatus: terminalStatus,
    });
  }, [terminalStatus, showStatus]);

  const backendQuery = useQuery<AdyenPaymentFromBackend, AdyenPaymentFromBackendVariables>(
    BACKEND_ADYEN_PAYMENT,
    {
      variables: {
        merchantReference: payment.id,
        venueId: venueId!,
      },
      pollInterval: 2000,
      skip: !payment.id,
    },
  );
  const backendPayment = backendQuery.data?.AdyenPayment;

  useEffect(() => {
    // This could maybe be its own hook for clarity, but in essence
    // what it does is that after the payment is done and this instance removed,
    // it will prevent the adyen call further down from triggering something
    // and causing buggy behaviour. We check if this is true and if it is, we
    // simply return and do nothing.
    return () => {
      hasCancelled.current = true;
    };
  }, []);

  useEffect(() => {
    if (!backendPayment) {
      return;
    }

    // Disregard if we've already finished the payment
    if (hasCancelled.current) {
      return;
    }

    logger.info('Got backend payment!', { payment: backendPayment });

    onComplete(null, {
      tip: backendPayment.tip,
      transactionId: backendPayment.pspReference,
      cardDetails: {
        cardType: backendPayment.method,
        maskedPan: backendPayment.methodIdentifier,
        aid: '',
        cardPaymentEntryMode: '',
        tsi: '',
        tvr: '',
      },
    });

    hasCancelled.current = true;
  }, [backendPayment]);

  /*
   * This effect sends off the initial payment request
   */
  useEffect(() => {
    const serviceId = Math.round(Math.random() * 1000000000).toString();

    onMetaChange({
      serviceId,
    });

    const variant = payment.variant || storageData?.adyenTerminals[0]?.POIID;

    logger.info(`Adyen: Starting payment of ${amount! / 100}`, {
      paymentId: payment.id,
      terminalId: variant,
      orderTotal: orderAmount - discount,
      order,
    });

    if (payment.meta.autocomplete) {
      logger.info('Autocompleting payment', { id: payment.id });
      onMetaChange(payment.meta);
      onComplete(null, {
        ...payment.meta,
      });
      return;
    }

    const terminal = adyenTerminals.find((t) => t.POIID === variant);

    if (!variant) {
      logger.warn('No terminal found', { id: payment.id });
      dispatch({
        type: actions.UPDATE_POS_PAYMENT,
        payment: {
          id: payment.id,
          hasConnectionIssues: true,
        },
      });
      return;
    }

    if (!terminal) {
      logger.warn('Terminal not found', { id: payment.id });
      return;
    }

    adyen
      .pay(variant, {
        amount: amount!,
        transactionId: payment.id,
        serviceId,
        saleId: 'device',
        socket,
        useSocket: isManagedTerminal,
      })
      .then((result) => {
        if (hasCancelled.current) {
          logger.info('Payment cancelled', { id: payment.id });
          return;
        }

        hasCancelled.current = true;

        logger.info('Adyen payment complete', { id: payment.id });
        onComplete(null, {
          tip: result.tip,
          transactionId: result.transactionId,
          cardDetails: result.cardDetails || undefined,
        });
      })
      .catch((reason) => {
        if (hasCancelled.current) {
          return;
        }

        hasCancelled.current = true;

        logger.warn(`Adyen payment failed, reason: ${reason}`, { id: payment.id });
        onFail(reason);
      });
  }, []);

  return null;
};

export default AdyenPayment;
